import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, X, Globe, Sparkles, MessageCircle, Target, BarChart3, Zap, Shield } from 'lucide-react';
export function PricingSlide() {
  const [customerValue, setCustomerValue] = useState(2500);
  const [targetCustomers, setTargetCustomers] = useState(8);
  const [selectedPlan, setSelectedPlan] = useState<'consult' | 'ads'>('consult');
  const planData = {
    consult: {
      price: 447,
      annual: 5364,
      setup: 1000,
      name: 'Consult',
      monthlyLeadRamp: [0.0, 0.0, 0.05, 0.1, 0.15, 0.25, 0.4, 0.65, 0.8, 0.9, 1.0, 1.0],
      note: 'Strategy consults compound — months 1-4 you build the foundation. Month 8+ is where the work pays off and leads flow consistently.'
    },
    ads: {
      price: 747,
      annual: 8964,
      setup: 1000,
      name: 'Ads',
      monthlyLeadRamp: [0.3, 0.55, 0.7, 0.8, 0.85, 0.9, 0.95, 1.0, 1.0, 1.0, 1.0, 1.0],
      note: 'Ads generate leads from week 1. Higher upfront ad spend, but by month 2 revenue covers all costs.'
    }
  };
  const current = planData[selectedPlan];
  const totalCost = current.annual + current.setup;
  const monthlyFullLeads = targetCustomers;
  const yearRevenue = current.monthlyLeadRamp.reduce((sum, ramp) => {
    return sum + monthlyFullLeads * ramp * customerValue;
  }, 0);
  const netProfit = yearRevenue - totalCost;
  let cumulativeRevenue = 0;
  let cumulativeCost = current.setup;
  let breakEvenMonth = 13;
  for (let m = 0; m < 12; m++) {
    cumulativeRevenue += monthlyFullLeads * current.monthlyLeadRamp[m] * customerValue;
    cumulativeCost += current.price;
    if (cumulativeRevenue >= cumulativeCost && breakEvenMonth === 13) {
      breakEvenMonth = m + 1;
    }
  }
  const formatCurrency = (val: number) => {
    if (Math.abs(val) >= 1000000) return (val < 0 ? '-' : '') + '$' + (Math.abs(val) / 1000000).toFixed(1) + 'M';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(val);
  };
  const pricingTiers = [{
    name: 'Consult',
    price: 447,
    desc: 'Strategy + organic growth for local businesses',
    features: ['2 hours/month strategy consult', 'AI-SEO content strategy', 'Advanced keyword targeting', 'Organic traffic growth', 'Performance dashboards'],
    excluded: ['Paid ads management'],
    cta: 'Start with Consult',
    href: 'https://letlois.com/grow',
    highlight: true,
    badge: 'MOST POPULAR',
    focusIcon: MessageCircle,
    focusLabel: 'Strategy',
    focusColor: 'text-green-400',
    focusBg: 'bg-green-500/10',
    footnote: null
  }, {
    name: 'Ads',
    price: 747,
    desc: 'Strategy + paid ads for fast lead generation',
    features: ['Everything in Consult', '1 campaign (Google or Facebook ads)', 'Campaign creation & testing', 'Landing page optimization', 'Dual traffic strategy'],
    excluded: [],
    cta: 'Choose Ads',
    href: 'https://letlois.com/boost',
    highlight: false,
    focusIcon: Target,
    focusLabel: 'Paid Growth',
    focusColor: 'text-red-400',
    focusBg: 'bg-red-500/10',
    footnote: '+$650/mo per additional campaign'
  }];
  return <section id="pricing" className="min-h-screen bg-dark py-24 px-4 flex flex-col justify-center" data-id="element-370">
      <div className="max-w-7xl mx-auto w-full" data-id="element-371">
        <div className="text-center mb-16" data-id="element-372">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-5xl font-bold mb-4" data-id="element-373">
            Two Plans. Pick Your Path.
          </motion.h2>
          <motion.p initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="text-xl text-text-muted" data-id="element-374">
            Choose the growth strategy that fits your business.
          </motion.p>
        </div>

        {/* Pro Plan - Big Deal Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="max-w-4xl mx-auto mb-12 bg-gradient-to-br from-primary/10 via-dark-card to-blue-900/10 border border-primary/20 rounded-2xl p-8 relative overflow-hidden" data-id="element-375">
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-[80px] pointer-events-none" data-id="element-376" />

          <div className="relative z-10 flex flex-col md:flex-row items-center gap-8" data-id="element-377">
            <div className="flex-shrink-0" data-id="element-378">
              <div className="w-20 h-20 rounded-2xl bg-primary/20 border border-primary/30 flex items-center justify-center" data-id="element-379">
                <Sparkles size={36} className="text-primary" data-id="element-380" />
              </div>
            </div>

            <div className="flex-1 text-center md:text-left" data-id="element-381">
              <div className="flex items-center justify-center md:justify-start gap-3 mb-2 flex-wrap" data-id="element-382">
                <h3 className="text-2xl font-bold text-white" data-id="element-383">
                  Pro Plan — Included on All Plans
                </h3>
                <span className="text-xs font-bold bg-primary/20 text-primary px-2 py-0.5 rounded-full uppercase tracking-wider" data-id="element-384">
                  Foundation
                </span>
              </div>
              <p className="text-text-muted text-lg mb-4" data-id="element-385">
                Every plan includes your{' '}
                <span className="text-white font-semibold" data-id="element-386">
                  custom website, CRM, and on-page SEO
                </span>{' '}
                — the full foundation that captures leads, tracks every
                touchpoint, and ranks you on Google.
              </p>
              <div className="flex flex-wrap gap-4 justify-center md:justify-start" data-id="element-387">
                <span className="flex items-center text-sm text-white/80" data-id="element-388">
                  <Shield size={14} className="text-primary mr-1.5" data-id="element-389" />
                  Custom 3-layer website
                </span>
                <span className="flex items-center text-sm text-white/80" data-id="element-390">
                  <BarChart3 size={14} className="text-primary mr-1.5" data-id="element-391" />
                  Full CRM platform
                </span>
                <span className="flex items-center text-sm text-white/80" data-id="element-392">
                  <Zap size={14} className="text-primary mr-1.5" data-id="element-393" />
                  On-page SEO
                </span>
                <span className="flex items-center text-sm text-white/80" data-id="element-394">
                  <Check size={14} className="text-primary mr-1.5" data-id="element-395" />
                  Foundations call
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Pricing Cards - 2 columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-20 max-w-4xl mx-auto" data-id="element-396">
          {pricingTiers.map((tier, index) => <motion.div key={tier.name} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} className={`relative flex flex-col p-6 rounded-2xl bg-dark-card border transition-all duration-300 ${tier.highlight ? 'border-primary shadow-[0_0_30px_rgba(26,139,246,0.15)] md:scale-105 z-10' : 'border-white/10 hover:border-white/20'}`} data-id="element-397">
              {tier.highlight && <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider" data-id="element-398">
                  {tier.badge}
                </div>}

              {/* Focus icon + label - top right */}
              <div className="absolute top-5 right-5" data-id="element-399">
                <div className={`flex items-center gap-1.5 ${tier.focusBg} px-2.5 py-1.5 rounded-lg`} data-id="element-400">
                  <tier.focusIcon size={14} className={tier.focusColor} data-id="element-401" />
                  <span className={`text-[10px] font-bold uppercase tracking-wider ${tier.focusColor}`} data-id="element-402">
                    {tier.focusLabel}
                  </span>
                </div>
              </div>

              <div className="mb-6 pr-4" data-id="element-403">
                <h3 className="text-2xl font-bold text-white mb-2" data-id="element-404">
                  {tier.name}
                </h3>
                <p className="text-sm text-text-muted mb-4 leading-relaxed pr-16" data-id="element-405">
                  {tier.desc}
                </p>
                <div className="flex items-baseline" data-id="element-406">
                  <span className="text-4xl font-bold text-primary" data-id="element-407">
                    ${tier.price}
                  </span>
                  <span className="text-text-dim ml-2" data-id="element-408">/mo</span>
                </div>
                {tier.footnote && <p className="text-[11px] text-text-dim mt-2 italic" data-id="element-409">
                    {tier.footnote}
                  </p>}
              </div>

              <div className="flex-1 space-y-3 mb-8" data-id="element-410">
                {tier.features.map((feature, i) => <div key={i} className="flex items-start text-sm text-white/90" data-id="element-411">
                    <Check size={16} className="text-green-500 mr-2 mt-0.5 shrink-0" data-id="element-412" />
                    <span data-id="element-413">{feature}</span>
                  </div>)}
                {tier.excluded.map((feature, i) => <div key={i} className="flex items-start text-sm text-text-dim/50" data-id="element-414">
                    <X size={16} className="text-red-500/50 mr-2 mt-0.5 shrink-0" data-id="element-415" />
                    <span data-id="element-416">{feature}</span>
                  </div>)}
              </div>

              <a href={tier.href} target="_blank" rel="noopener noreferrer" className={`w-full py-3 rounded-full font-bold transition-all text-center block ${tier.highlight ? 'bg-primary hover:bg-primary-hover text-white shadow-lg shadow-primary/25' : 'bg-white/10 hover:bg-white/20 text-white'}`} data-id="element-417">
                {tier.cta}
              </a>
            </motion.div>)}
        </div>

        {/* ROI Calculator */}
        <div className="bg-gradient-to-br from-dark-card to-primary/5 rounded-2xl p-8 border border-white/10 mb-16" data-id="element-418">
          <h3 className="text-2xl font-bold mb-2 text-center" data-id="element-419">
            Calculate Your ROI
          </h3>
          <p className="text-text-muted text-center text-sm mb-8" data-id="element-420">
            Conservative projections based on Ahrefs, WordStream & CallRail
            industry data
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8" data-id="element-421">
            <div className="space-y-6" data-id="element-422">
              <div data-id="element-423">
                <label className="block text-sm font-medium text-text-muted mb-2" data-id="element-424">
                  Average Customer Value
                </label>
                <div className="relative" data-id="element-425">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-text-dim" data-id="element-426">
                    $
                  </span>
                  <input type="number" value={customerValue} onChange={e => setCustomerValue(Number(e.target.value))} className="w-full bg-black/30 border border-white/10 rounded-xl py-3 pl-8 pr-4 text-white focus:border-primary focus:outline-none transition-colors" data-id="element-427" />
                </div>
              </div>

              <div data-id="element-428">
                <label className="block text-sm font-medium text-text-muted mb-2" data-id="element-429">
                  Target New Customers / Month
                </label>
                <input type="number" value={targetCustomers} onChange={e => setTargetCustomers(Number(e.target.value))} className="w-full bg-black/30 border border-white/10 rounded-xl py-3 px-4 text-white focus:border-primary focus:outline-none transition-colors" data-id="element-430" />
              </div>

              <div data-id="element-431">
                <label className="block text-sm font-medium text-text-muted mb-2" data-id="element-432">
                  Select Plan
                </label>
                <div className="grid grid-cols-2 gap-2" data-id="element-433">
                  {(['consult', 'ads'] as const).map(plan => <button key={plan} onClick={() => setSelectedPlan(plan)} className={`py-2 rounded-lg text-sm font-medium transition-all ${selectedPlan === plan ? 'bg-primary text-white shadow-lg shadow-primary/20' : 'bg-white/5 text-text-muted hover:bg-white/10'}`} data-id="element-434">
                      {plan.charAt(0).toUpperCase() + plan.slice(1)}
                    </button>)}
                </div>
              </div>
            </div>

            <div className="lg:col-span-2 space-y-4" data-id="element-435">
              <div className="bg-black/20 rounded-xl p-6 border border-white/5" data-id="element-436">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-437">
                  <div data-id="element-438">
                    <p className="text-xs text-text-dim uppercase tracking-wider mb-1" data-id="element-439">
                      Year 1 Revenue
                    </p>
                    <p className="text-2xl font-bold text-white" data-id="element-440">
                      {formatCurrency(yearRevenue)}
                    </p>
                  </div>
                  <div data-id="element-441">
                    <p className="text-xs text-text-dim uppercase tracking-wider mb-1" data-id="element-442">
                      Total Investment
                    </p>
                    <p className="text-2xl font-bold text-white" data-id="element-443">
                      {formatCurrency(totalCost)}
                    </p>
                  </div>
                  <div data-id="element-444">
                    <p className="text-xs text-text-dim uppercase tracking-wider mb-1" data-id="element-445">
                      Net Profit
                    </p>
                    <p className={`text-2xl font-bold ${netProfit >= 0 ? 'text-green-400' : 'text-red-400'}`} data-id="element-446">
                      {formatCurrency(netProfit)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Break-even highlight */}
              <motion.div key={selectedPlan + customerValue + targetCustomers} initial={{
              scale: 0.98,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} className={`rounded-xl p-5 border ${breakEvenMonth <= 12 ? 'bg-green-500/10 border-green-500/20' : 'bg-yellow-500/10 border-yellow-500/20'}`} data-id="element-447">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-448">
                  <div data-id="element-449">
                    <p className={`font-bold text-lg ${breakEvenMonth <= 12 ? 'text-green-400' : 'text-yellow-400'}`} data-id="element-450">
                      {breakEvenMonth <= 12 ? `Break-even: Month ${breakEvenMonth}` : 'Break-even: 12+ months'}
                      {breakEvenMonth <= 12 && selectedPlan === 'consult' && ' — then profit skyrockets'}
                    </p>
                    <p className="text-text-muted text-sm mt-1" data-id="element-451">
                      {current.note}
                    </p>
                  </div>

                  {/* Mini month timeline */}
                  <div className="flex items-end gap-1" data-id="element-452">
                    {current.monthlyLeadRamp.map((ramp, i) => <div key={i} className="flex flex-col items-center" data-id="element-453">
                        <div className={`w-3 rounded-t-sm ${i < breakEvenMonth ? 'bg-red-500/40' : 'bg-green-500/60'}`} style={{
                      height: `${Math.max(ramp * 32, 2)}px`
                    }} data-id="element-454" />
                        {(i === 0 || i === 5 || i === 11) && <span className="text-[7px] text-text-dim mt-0.5" data-id="element-455">
                            {i + 1}
                          </span>}
                      </div>)}
                    <span className="text-[7px] text-text-dim ml-0.5 self-end mb-0.5" data-id="element-456">
                      mo
                    </span>
                  </div>
                </div>
              </motion.div>

              {/* Plan-specific insight */}
              <div className="bg-blue-900/10 border border-blue-500/15 rounded-lg px-4 py-3" data-id="element-457">
                <p className="text-xs text-blue-300" data-id="element-458">
                  <span className="font-bold" data-id="element-459">
                    {current.name} Plan Insight:
                  </span>{' '}
                  {selectedPlan === 'consult' && "Strategy + SEO is a compounding asset. Months 1-4 you're building domain authority and refining strategy — expect minimal leads. Months 5-7 rankings start climbing. Month 8+ is where it pays off big. Average local SEO ROI after 12 months: 200-300% (Ahrefs)."}
                  {selectedPlan === 'ads' && 'Google or Facebook Ads for local services average $30-80 CPA with 3-5x ROAS (WordStream). Ads produce leads from week 1 while SEO builds behind the scenes. Month 2 typically covers all costs including ad spend.'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Caveat & Trust */}
        <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-460">
          <div className="bg-blue-900/10 border border-blue-500/20 rounded-xl p-4 mb-8" data-id="element-461">
            <p className="text-sm text-blue-200" data-id="element-462">
              <span className="font-bold" data-id="element-463">Honest Note:</span> We're built on
              GoHighLevel—the most powerful (and least supported) platform in
              the world. That's why we exist. We're the support team you need.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-6 text-sm text-text-dim" data-id="element-464">
            <span className="flex items-center" data-id="element-465">
              <Check size={16} className="text-primary mr-2" data-id="element-466" /> Join 200+
              businesses
            </span>
            <span className="flex items-center" data-id="element-467">
              <Check size={16} className="text-primary mr-2" data-id="element-468" /> Setup support
              included
            </span>
            <span className="flex items-center" data-id="element-469">
              <Check size={16} className="text-primary mr-2" data-id="element-470" /> Money-back
              guarantee
            </span>
          </div>
        </div>

        {/* Final CTA */}
        <div className="text-center" data-id="element-471">
          <motion.button whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} onClick={() => {
          document.getElementById('pricing')?.scrollIntoView({
            behavior: 'smooth'
          });
        }} className="bg-primary hover:bg-primary-hover text-white text-xl font-bold py-5 px-10 rounded-full shadow-xl shadow-primary/30 mb-4 flex items-center mx-auto" data-id="element-472">
            <Globe className="mr-3" data-id="element-473" />
            Schedule an Onboarding Call
          </motion.button>
          <p className="text-text-muted" data-id="element-474">Pick a plan above to get started.</p>
        </div>
      </div>
    </section>;
}